import { getMe, ownerDoesJobs, worksAlone } from '../me'
import { styleSelect } from '../styled-select'
import { api, ApiError, changeNow, flushQueue, getQueue, mutate, onQueueChange, readCache, withdraw } from '../api'
import { monthOf, monthRange, shiftMonth } from '../month-view'
import { routeGen } from '../route-state'
import { offerUndo } from '../undo'
import { addDaysISO, clear, dayLabel, el, loadError, toast, todayISO } from '../ui'
import {
  clashLines,
  dayInfo,
  dayLabelText,
  daysWorking,
  everyWords,
  layWaitingDays,
  noWorkingDays,
  SET_YOUR_DAYS,
  TOO_SOON,
  visitsOn,
  visitsWords,
  WEEK_ORDER,
  WEEKDAYS,
  workingDatesIn,
  type DayInfo,
  type WorkDaysPayload,
  type WorkRule,
} from '../work-days-view'
import '../month.css'
import '../my-days.css'
import '../my-days-alone.css'

// My days (migration 0040; Liam, 1 Oct 2026, about Sendi: "he logs in to book a day of work in, he needs to book it
// in a week in advance. On here he can specify every Friday but then take certain ones off. Also a month view
// similar to what we have already so he and us can see his schedule"). The worker's month, in the Month view's
// look: his days, whose the other days are, booking or taking off a day a week or more ahead, and a repeating day.
// Every change needs signal and an answer now (changeNow): a booking kept for later could land inside the week.
//
// A one-person round (0078; Liam, 19 Oct 2026) sets its working days only here, as "My working days": a day off or a
// day worked is an ordinary save (the offline queue, with Undo), says how many visits are booked that day and that they
// stay, and a holiday is "Take days off from ... to ..." in one go. The month is kept on the phone so the page opens
// with no signal. No week's notice (owners change their own days from today), and nobody else's days.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

let first = monthOf(todayISO())
let payload: WorkDaysPayload | null = null
let picked: string | null = null
let busy = false
let notes: string[] = []
const repeatDraft = new Set<number>()
let sequence = 0
// A one-person round: when the month on show is the copy saved on this phone (no signal), and the holiday being typed.
let savedAt: number | null = null
let offFrom = ''
let offTo = ''

export function mountMyDays(): void {
  if (first < monthOf(todayISO())) first = monthOf(todayISO())
  picked = null
  notes = []
  repeatDraft.clear()
  offFrom = ''
  offTo = ''
  if (worksAlone()) bindAlone()
  void load()
}

const savedKey = (from: string): string => `my-days:${from}`

async function load(): Promise<void> {
  const seq = ++sequence
  const gen = routeGen
  const root = document.querySelector<HTMLElement>('#screen')!
  const range = monthRange(first)
  const alone = worksAlone()
  try {
    const p = await api<WorkDaysPayload>(`/api/work-days?from=${range.from}&to=${range.to}`, alone ? savedKey(range.from) : undefined)
    if (gen !== routeGen || seq !== sequence) return
    payload = p
    savedAt = null
    render(root)
  } catch (err) {
    if (gen !== routeGen || seq !== sequence) return
    const saved = alone ? readCache<WorkDaysPayload>(savedKey(range.from)) : null
    if (saved) {
      payload = saved.data
      savedAt = saved.at
      render(root)
      return
    }
    clear(root)
    root.append(heading())
    loadError(root, `${(err as Error).message}. ${alone ? 'My working days' : 'My days'} needs signal.`, () => void load(), undefined, alone ? 'This month' : 'My days')
  }
}

function heading(): HTMLElement {
  if (worksAlone()) {
    return el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'My working days'), el('p', { class: 'muted' }, 'The days you work. Messages offers these days to customers. Tap a date to take it off or work it.')))
  }
  return el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'My days'), el('p', { class: 'muted' }, ownerDoesJobs() ? 'Set your usual working week once. Tap any date to work that day or take it off. Messages can offer these days even before you have jobs booked.' : 'The days you work. Book a day at least a week ahead. Tap a day to book it or take it off.')))
}

// Once: a change that waited for signal landing (or refused) draws the month again, from the server.
let aloneBound = false
function bindAlone(): void {
  if (aloneBound) return
  aloneBound = true
  const here = (): boolean => window.location.hash.startsWith('#/my-days') && worksAlone()
  let waitingBefore = waitingDays()
  onQueueChange(() => {
    const now = waitingDays()
    const changed = now !== waitingBefore
    waitingBefore = now
    if (changed && here() && !busy) render(document.querySelector<HTMLElement>('#screen')!)
  })
  window.addEventListener('dropby:data-changed', () => { if (here() && !busy) void load() })
}

function waitingDays(): number {
  return getQueue().filter((q) => q.path === '/api/work-days' || q.path === '/api/work-days/undo').length
}

function render(root: HTMLElement): void {
  if (!payload) return
  const alone = worksAlone()
  // A one-person round: the days off and days worked still waiting for signal, laid on.
  const laid = alone ? layWaitingDays(payload, getQueue()) : { payload, waiting: 0 }
  const p = laid.payload
  const keepY = window.scrollY
  clear(root)
  root.append(heading())
  const range = monthRange(first)
  const [y, mo] = first.split('-').map(Number) as [number, number]

  // ---- month arrows ----
  const bar = el('div', { class: 'screen-bar month-bar' })
  const step = (label: string, glyph: string, n: number): HTMLElement => {
    const b = el('button', { type: 'button', class: 'week-step', 'aria-label': label, title: label }, glyph)
    b.addEventListener('click', () => { first = shiftMonth(first, n); picked = null; void load() })
    return b
  }
  const back = step('Previous month', '‹', -1)
  if (first <= monthOf(todayISO())) back.setAttribute('disabled', 'disabled')
  const thisMonth = el('button', { type: 'button' }, 'This month')
  thisMonth.addEventListener('click', () => { first = monthOf(todayISO()); picked = null; void load() })
  bar.append(el('div', { class: 'month-nav' }, back, el('h3', { class: 'month-title' }, `${MONTHS[mo - 1]} ${y}`), step('Next month', '›', 1)), thisMonth)
  root.append(bar)

  const working = daysWorking(p, first.slice(0, 7))
  root.append(el('div', { class: 'month-stats' },
    el('div', { class: 'month-stat' }, el('b', {}, String(working)), el('span', {}, `Day${working === 1 ? '' : 's'} working in ${MONTHS[mo - 1]}`)),
    // Always today for a one-person round's owner, so not said.
    ...(alone ? [] : [el('div', { class: 'month-stat' }, el('b', {}, dayLabel(p.earliest)), el('span', {}, 'First day you can book'))])))
  root.append(el('div', { class: 'month-filters' }, el('span', { class: 'month-key myday-key' },
    el('span', {}, el('i', { class: 'myday-swatch myday-mine' }), 'Working'),
    ...(alone ? [] : [el('span', {}, el('i', { class: 'myday-swatch myday-theirs' }), "Someone else's day")]),
    el('span', {}, el('i', { class: 'myday-swatch myday-off' }), alone ? 'Day off' : 'Repeating day taken off'),
    ...(ownerDoesJobs() ? [] : [el('span', {}, el('i', { class: 'myday-swatch myday-soon' }), 'Less than a week away')]))))

  if (alone && noWorkingDays(p)) root.append(el('p', { class: 'myday-note myday-none', role: 'status' }, SET_YOUR_DAYS))
  if (laid.waiting) root.append(el('p', { class: 'myday-note myday-waiting', role: 'status' }, laid.waiting === 1 ? 'Waiting to send. It sends by itself when there\u2019s signal.' : 'Waiting to send. They send by themselves when there\u2019s signal.'))
  for (const line of notes) root.append(el('p', { class: 'myday-note', role: 'status' }, line))

  // ---- the grid ----
  const cal = el('div', { class: 'month-grid myday-grid', role: 'grid', 'aria-label': `${MONTHS[mo - 1]} ${y}` })
  for (const d of WEEK_ORDER) cal.append(el('div', { class: 'month-dow', role: 'columnheader' }, WEEKDAYS[d].slice(0, 3)))
  const today = todayISO()
  for (let d = range.from; d <= range.to; d = addDays(d, 1)) cal.append(dayCell(dayInfo(d, p), d.slice(0, 7) === first.slice(0, 7), today, root))
  root.append(cal)

  if (picked && picked >= range.from && picked <= range.to) root.append(dayPanel(dayInfo(picked, p), root, p))
  root.append(repeatPanel(p, root))
  if (alone) root.append(holidayPanel(root))
  // The month as this phone saved it: say so, and how old it is.
  if (savedAt !== null) loadError(root, 'no connection', () => void load(), savedAt, 'This month')
  window.scrollTo({ top: keepY })
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

function dayCell(info: DayInfo, inMonth: boolean, today: string, root: HTMLElement): HTMLElement {
  const dow = new Date(`${info.date}T12:00:00Z`).getUTCDay()
  const cell = el('div', {
    class: `month-day myday-day myday-${info.kind}${inMonth ? '' : ' month-day-out'}${info.date === today ? ' month-today' : ''}${info.past ? ' month-past myday-past' : ''}${!info.changeable && !info.past ? ' myday-soon' : ''}${dow === 0 || dow === 6 ? ' month-weekend' : ''}${picked === info.date ? ' month-picked' : ''}`,
    role: 'gridcell',
    tabindex: '0',
    'data-date': info.date,
  })
  const words = dayLabelText(info)
  cell.setAttribute('aria-label', `${dayLabel(info.date)}${words ? `: ${words}` : ''}${info.repeating && info.kind === 'mine' ? ', repeating' : ''}`)
  cell.append(el('div', { class: 'month-day-head' }, el('span', { class: 'month-num' }, String(Number(info.date.slice(8))))))
  if (words) cell.append(el('span', { class: 'myday-tag' }, words))
  if (info.repeating && info.kind === 'mine') cell.append(el('span', { class: 'myday-repeat', title: 'Repeating day' }, '↻'))
  const open = (): void => { picked = info.date; render(root); root.querySelector('.myday-panel')?.scrollIntoView({ block: 'nearest' }) }
  cell.addEventListener('click', open)
  cell.addEventListener('keydown', (e) => { if (e.key === 'Enter') open() })
  return cell
}

function dayPanel(info: DayInfo, root: HTMLElement, p: WorkDaysPayload): HTMLElement {
  const alone = worksAlone()
  const box = el('section', { class: 'month-daylist myday-panel', 'aria-label': dayLabel(info.date) })
  const say = (text: string): void => { box.append(el('p', {}, text)) }
  box.append(el('div', { class: 'month-daylist-head' }, el('h3', {}, dayLabel(info.date))))
  if (info.kind === 'mine') say(info.repeating ? "You're working. It's one of your repeating days." : "You're working.")
  else if (info.kind === 'theirs') say(`This is ${info.who}'s day.`)
  else if (info.kind === 'off') say(alone ? 'Day off. It\u2019s one of your repeating days.' : 'One of your repeating days, taken off.')
  else say(alone ? 'Not a working day.' : 'Nobody is booked.')
  // How many visits are booked that day; on a day off, that they stay.
  const booked = alone ? visitsWords(visitsOn(p, [info.date]), 1, info.kind !== 'mine') : ''
  if (booked) say(booked)

  if (info.past) return box
  if (!info.changeable) {
    if (info.kind !== 'theirs') box.append(el('p', { class: 'myday-why' }, TOO_SOON))
    return box
  }
  if (info.kind === 'theirs') return box
  const book = info.kind !== 'mine'
  const btn = el('button', { type: 'button', class: book ? 'btn-primary myday-action' : 'btn-danger myday-action' }, book ? (alone ? 'Work this day' : 'Book this day') : 'Take this day off')
  if (busy) btn.setAttribute('disabled', 'disabled')
  btn.addEventListener('click', () => {
    if (alone) void saveDays(book ? 'POST' : 'DELETE', [info.date], p, root)
    else void change(book ? 'POST' : 'DELETE', '/api/work-days', { date: info.date }, book ? `Booked ${dayLabel(info.date)}` : `${dayLabel(info.date)} taken off`, root)
  })
  box.append(btn)
  return box
}

// "Take days off from ... to ...": a holiday in one go (one-person round). Every working day between the two dates comes
// off; visits booked on them stay.
function holidayPanel(root: HTMLElement): HTMLElement {
  const box = el('section', { class: 'month-daylist myday-holiday', 'aria-label': 'Days off' })
  box.append(el('div', { class: 'month-daylist-head' }, el('h3', {}, 'Days off')))
  const today = todayISO()
  const from = el('input', { type: 'date', min: today, value: offFrom, 'aria-label': 'First day off' }) as HTMLInputElement
  const to = el('input', { type: 'date', min: offFrom || today, value: offTo, 'aria-label': 'Last day off' }) as HTMLInputElement
  from.addEventListener('input', () => { offFrom = from.value; to.min = offFrom || today; if (offTo && offTo < offFrom) { offTo = offFrom; to.value = offTo } })
  to.addEventListener('input', () => { offTo = to.value })
  box.append(el('p', { class: 'myday-range' }, el('label', {}, 'Take days off from ', from), el('label', {}, ' to ', to)))
  const go = el('button', { type: 'button', class: 'btn-danger myday-action' }, 'Take these days off')
  if (busy) go.setAttribute('disabled', 'disabled')
  go.addEventListener('click', () => void takeHoliday(root))
  box.append(go)
  return box
}

async function takeHoliday(root: HTMLElement): Promise<void> {
  if (busy || !payload) return
  const today = todayISO()
  const from = offFrom
  const to = offTo || offFrom
  notes = []
  if (!from) { notes = ['Pick the first day off.']; render(root); return }
  if (from < today) { notes = ['Days off can start today or later.']; render(root); return }
  if (to < from) { notes = ['The last day off is before the first.']; render(root); return }
  if (to > addDaysISO(from, 61)) { notes = ['Pick up to 62 days at a time.']; render(root); return }
  // Their working days between the two dates, read fresh (the holiday may run past the month on show). With no signal,
  // the month on show will do when the holiday is inside it.
  let p: WorkDaysPayload | null = null
  busy = true
  render(root)
  try {
    p = await api<WorkDaysPayload>(`/api/work-days?from=${from}&to=${to}`)
  } catch {
    const shown = monthRange(first)
    if (from >= shown.from && to <= shown.to) p = payload
  } finally { busy = false }
  if (!p) { notes = ['Days off past this month need signal. Try again when you have it.']; render(root); return }
  const dates = workingDatesIn(layWaitingDays(p, getQueue()).payload, from, to)
  if (!dates.length) {
    notes = [from === to ? `${dayLabel(from)} isn\u2019t a working day, so there is nothing to take off.` : `No working days from ${dayLabel(from)} to ${dayLabel(to)}, so there is nothing to take off.`]
    render(root)
    return
  }
  offFrom = ''
  offTo = ''
  await saveDays('DELETE', dates, p, root, from === to ? undefined : `${dayLabel(from)} to ${dayLabel(to)}`)
}

// A day off or a day worked, or several (a holiday), as an ordinary save: sent now, or kept on the phone and sent when
// there is signal. Undo names the save and Roundly reverses only the days it changed, leaving any day changed since (Sol's
// review); if the save has not left the phone yet, it is simply taken back.
async function saveDays(method: 'POST' | 'DELETE', dates: string[], p: WorkDaysPayload, root: HTMLElement, span?: string): Promise<void> {
  if (busy || !dates.length) return
  const gen = routeGen
  const one = dates.length === 1
  const what = method === 'DELETE'
    ? (span ? `Days off: ${span}` : `${dayLabel(dates[0]!)} taken off`)
    : (one ? `Working ${dayLabel(dates[0]!)}` : `Working ${dates.length} days`)
  const visits = method === 'DELETE' ? visitsWords(visitsOn(p, dates), one ? 1 : 'several', true) : ''
  busy = true
  notes = []
  // The save's own key, read off the queue the moment mutate records it (before anything is awaited): Undo names the
  // save by it, and the server reverses only what that save changed (functions/api/work-days/undo.ts).
  const queued = new Set(getQueue().map((q) => q.key))
  const saving = mutate({ label: what, method, path: '/api/work-days', body: one ? { date: dates[0] } : { dates } })
  const key = getQueue().find((q) => !queued.has(q.key) && q.path === '/api/work-days')?.key ?? null
  const ack = await saving
  busy = false
  // No signal, or a server error: kept on the phone and sent again by itself, like any save.
  const waiting = (k: string | null): boolean => !!k && getQueue().some((q) => q.key === k && q.status !== 'conflict' && q.status !== 'failed')
  if (!ack.ok && !waiting(key)) {
    notes = [ack.error ?? 'That didn\u2019t save. Try again.']
    if (gen === routeGen) await load()
    else toast(notes[0]!)
    return
  }
  if (visits) notes = [visits]
  const onPage = (): boolean => window.location.hash.startsWith('#/my-days')
  if (key) offerUndo(span && method === 'DELETE' ? `${dates.length} day${one ? '' : 's'} off, ${span}` : what, () => {
    notes = []
    // Still on the phone and never sent: take it back rather than send both.
    if (withdraw(key)) { if (onPage()) render(root); return }
    void (async () => {
      // Sent but not answered yet: given its chance to land first, so the Undo never reaches Roundly before it.
      if (getQueue().some((q) => q.key === key)) await flushQueue()
      let undoKey: string | null = null
      const undone = await mutate<{ left?: string[]; words?: string | null }>({ label: `Undo: ${what}`, method: 'POST', path: '/api/work-days/undo', body: { key }, onQueued: (m) => { undoKey = m.key } })
      const kept = waiting(undoKey)
      // A day changed since (another phone, the Diary) is left as it is, and said.
      if (undone.ok && undone.row?.words) notes = [undone.row.words]
      if (!undone.ok && !kept) { notes = [undone.error ?? 'Undo didn\u2019t save.']; if (!onPage()) toast(notes[0]!) }
      if (onPage()) { if (kept) render(root); else await load() }
    })()
  })
  if (gen !== routeGen) return
  if (ack.ok) await load()
  else render(root)
}

function repeatPanel(p: WorkDaysPayload, root: HTMLElement): HTMLElement {
  const box = el('section', { class: 'month-daylist myday-repeats', 'aria-label': 'Repeating days' })
  box.append(el('div', { class: 'month-daylist-head' }, el('h3', {}, 'Repeating days')))
  box.append(el('p', { class: 'muted' }, ownerDoesJobs() ? 'Choose the days you normally work. They repeat from today onwards; take individual dates off in the calendar.' : 'Books that day every week, from a week ahead. Take any one off in the month above.'))
  const mine = p.rules.filter((r) => r.worker === p.worker)
  for (const r of mine) box.append(ruleRow(r, root))
  const left = WEEK_ORDER.filter((d) => !mine.some((r) => r.weekday === d))
  if (left.length && ownerDoesJobs()) {
    const days = el('fieldset', { class: 'myday-week', 'aria-label': 'Working days to add' }, el('legend', {}, 'Add your usual working days'))
    for (const d of left) {
      const input = el('input', { type: 'checkbox', value: String(d) }) as HTMLInputElement
      input.checked = repeatDraft.has(d)
      input.disabled = busy
      input.addEventListener('change', () => { if (input.checked) repeatDraft.add(d); else repeatDraft.delete(d) })
      days.append(el('label', { class: 'myday-week-choice' }, input, el('span', {}, WEEKDAYS[d])))
    }
    const save = el('button', { type: 'button', class: 'btn-primary myday-action' }, busy ? 'Saving…' : 'Save working days')
    save.disabled = busy
    save.addEventListener('click', () => {
      const wanted = left.filter(d => repeatDraft.has(d))
      if (!wanted.length) { toast('Choose at least one working day'); return }
      void saveWorkingWeek(wanted, root)
    })
    box.append(days, save)
  } else if (left.length) {
    const pick = el('select', { 'aria-label': 'Day to repeat', class: 'myday-select' })
    for (const d of left) pick.append(el('option', { value: String(d) }, everyWords(d)))
    const add = el('button', { type: 'button', class: 'btn-primary' }, 'Repeat every week')
    if (busy) add.setAttribute('disabled', 'disabled')
    add.addEventListener('click', () => {
      const weekday = Number(pick.value)
      void change('POST', '/api/work-days/rules', { weekday }, `${everyWords(weekday)} booked`, root)
    })
    box.append(el('div', { class: 'myday-add' }, pick, add))
    styleSelect(pick)
  }
  return box
}

function ruleRow(r: WorkRule, root: HTMLElement): HTMLElement {
  const row = el('div', { class: 'month-row myday-rule' })
  row.append(el('span', { class: 'month-row-who' }, everyWords(r.weekday), el('span', { class: 'month-row-state' }, ` · from ${dayLabel(r.starts_on)}`)))
  const stop = el('button', { type: 'button', class: 'month-move' }, 'Stop')
  if (busy) stop.setAttribute('disabled', 'disabled')
  stop.addEventListener('click', () => {
    if (!window.confirm(`Stop ${everyWords(r.weekday).toLowerCase()}? ${ownerDoesJobs() ? "Those working days come off from today. Customer visits stay in the diary." : "Those days come off from a week ahead."}`)) return
    void change('DELETE', `/api/work-days/rules/${r.id}`, { version: r.version }, `${everyWords(r.weekday)} stopped`, root)
  })
  row.append(stop)
  return row
}

async function change(method: 'POST' | 'DELETE', path: string, body: unknown, done: string, root: HTMLElement): Promise<void> {
  if (busy) return
  // The screen this change was made on. If he has moved on (to Today) by the time the answer comes, My days is not
  // drawn again over whatever is showing now; the toast still says what happened.
  const gen = routeGen
  busy = true
  notes = []
  render(root)
  try {
    const answer = await changeNow<{ clashes?: { date: string; worker: string }[] }>(method, path, body)
    notes = clashLines(answer.clashes ?? [], dayLabel)
    toast(done)
  } catch (err) {
    const e = err as ApiError
    notes = [e.message]
    if (gen !== routeGen) toast(e.message)
  } finally {
    busy = false
  }
  if (gen !== routeGen) return
  await load()
}

async function saveWorkingWeek(weekdays: number[], root: HTMLElement): Promise<void> {
  if (busy) return
  const identity = getMe()
  const gen = routeGen
  busy = true
  notes = []
  render(root)
  let saved = 0
  try {
    for (const weekday of weekdays) {
      if (getMe()?.businessId !== identity?.businessId || getMe()?.email !== identity?.email) throw new Error('Your round or sign-in changed')
      const answer = await changeNow<{ clashes?: { date: string; worker: string }[] }>('POST', '/api/work-days/rules', { weekday })
      repeatDraft.delete(weekday)
      saved++
      notes.push(...clashLines(answer.clashes ?? [], dayLabel))
    }
    toast('Working week saved')
  } catch (err) {
    const message = `${saved ? `${saved} working day${saved === 1 ? '' : 's'} saved. ` : ''}${(err as Error).message}. Your remaining choices are kept; try again.`
    notes.push(message)
    if (gen !== routeGen) toast(message)
  } finally { busy = false }
  if (gen === routeGen) await load()
}
